import { StorageService } from "@/services/storage.service";
import { getSharedRefresh } from "@/lib/api";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3002";
const HEALTHCHECK_TIMEOUT_MS = 8000;
const IDLE_THRESHOLD_MS = 10 * 60 * 1000;
const REFRESH_EARLY_WINDOW_MS = 5 * 60 * 1000;
const MIN_WARMUP_INTERVAL_MS = 60 * 1000;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return JSON.parse(window.atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function getTokenExpiryMs(token: string | null): number | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== "number") return null;
  return exp * 1000;
}

class AppWarmupService {
  private lastActivityAt = Date.now();
  private lastWarmupAt = 0;
  private warmupPromise: Promise<void> | null = null;

  markActivity() {
    this.lastActivityAt = Date.now();
  }

  shouldWarmUpNow() {
    const now = Date.now();
    const idleTime = now - this.lastActivityAt;
    const recentlyWarmed = now - this.lastWarmupAt < MIN_WARMUP_INTERVAL_MS;
    return idleTime >= IDLE_THRESHOLD_MS && !recentlyWarmed;
  }

  async warmUpIfNeeded() {
    if (!this.shouldWarmUpNow()) return;
    await this.warmUp();
  }

  async warmUp() {
    if (this.warmupPromise) {
      return this.warmupPromise;
    }

    this.warmupPromise = (async () => {
      this.lastWarmupAt = Date.now();

      await this.pingHealthcheck();

      if (this.shouldRefreshSoon()) {
        await this.refreshSession().catch(() => {
          // O fluxo normal da aplicação continua tratando 401/refresh depois.
        });
      }
    })().finally(() => {
      this.warmupPromise = null;
    });

    return this.warmupPromise;
  }

  private shouldRefreshSoon() {
    const refreshToken = StorageService.getRefreshToken();
    if (!refreshToken) return false;

    const accessToken = StorageService.getAccessToken();
    const expiresAt = getTokenExpiryMs(accessToken);
    if (!expiresAt) return true;

    return expiresAt - Date.now() <= REFRESH_EARLY_WINDOW_MS;
  }

  private async pingHealthcheck() {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), HEALTHCHECK_TIMEOUT_MS);

    try {
      await fetch(`${API_URL}/health`, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
    } catch {
      // A ideia aqui é só acordar a infra; não bloquear a UX por falha no ping.
    } finally {
      window.clearTimeout(timeout);
    }
  }

  private async refreshSession() {
    // Usa a mesma deduplicação de refresh do interceptor em `web/src/lib/api.ts`
    // para evitar refresh concorrente (que pode causar 401 por "refresh token já usado").
    await getSharedRefresh();
  }
}

export const appWarmupService = new AppWarmupService();
